// Apolo IDE — PlanBlockBody (sprint plan-tab-v1, E2 + E3 enrichments).
//
// Roteia pra renderer específico por kind. E3 adiciona MarkdownBlockEditor
// (CodeMirror) + MermaidBlockBody + TableBlockBody + EmbedBlockBody +
// ChatRefBlockBody. Demais blocks ficam inline aqui (small enough).

import { useState } from 'react';
import * as Icons from 'lucide-react';
import { apiUrl, getStoredToken } from '../../lib/api.js';
import { getBlockKindMeta } from '../../lib/plan/blockKinds.js';
import MarkdownBlockEditor from './blocks/MarkdownBlockEditor.jsx';
import MermaidBlockBody from './blocks/MermaidBlockBody.jsx';
import TableBlockBody from './blocks/TableBlockBody.jsx';
import EmbedBlockBody from './blocks/EmbedBlockBody.jsx';
import ChatRefBlockBody from './blocks/ChatRefBlockBody.jsx';
import PdfBlockBody from './blocks/PdfBlockBody.jsx';
import ChartBlockBody from './blocks/ChartBlockBody.jsx';

export default function PlanBlockBody({ block, accent, onPayloadChange }) {
  const payload = block.payload || {};

  switch (block.kind) {
    case 'text':
      return <TextBody payload={payload} onChange={onPayloadChange} />;
    case 'sticky':
      return <StickyBody payload={payload} onChange={onPayloadChange} accent={accent} />;
    case 'heading':
      return <HeadingBody payload={payload} onChange={onPayloadChange} />;
    case 'todo':
      return <TodoBody payload={payload} onChange={onPayloadChange} />;
    case 'image':
      return <ImageBody payload={payload} onChange={onPayloadChange} />;
    case 'video':
      return <VideoBody payload={payload} onChange={onPayloadChange} />;
    case 'link':
      return <LinkBody payload={payload} onChange={onPayloadChange} />;
    case 'file':
      return <FileBody block={block} payload={payload} accent={accent} />;
    case 'pdf':
      return <PdfBlockBody block={block} payload={payload} accent={accent} />;
    case 'skill':
      return <SkillBody payload={payload} onChange={onPayloadChange} accent={accent} />;
    case 'divider':
      return <DividerBody payload={payload} onChange={onPayloadChange} accent={accent} />;
    case 'mermaid':
      return <MermaidBlockBody payload={payload} onChange={onPayloadChange} accent={accent} />;
    case 'table':
      return <TableBlockBody payload={payload} onChange={onPayloadChange} accent={accent} />;
    case 'embed':
      return <EmbedBlockBody payload={payload} onChange={onPayloadChange} accent={accent} />;
    case 'chat-ref':
      return <ChatRefBlockBody payload={payload} onChange={onPayloadChange} accent={accent} />;
    case 'chart':
      return <ChartBlockBody block={block} payload={payload} onChange={onPayloadChange} />;
    default:
      return <PlaceholderBody kind={block.kind} accent={accent} />;
  }
}

function TextBody({ payload, onChange }) {
  return (
    <MarkdownBlockEditor
      value={payload.body || ''}
      onChange={(body) => onChange({ ...payload, body })}
    />
  );
}

function StickyBody({ payload, onChange, accent }) {
  const [body, setBody] = useState(payload.body || '');
  return (
    <div className="h-full w-full" style={{ background: `${accent}1a` }}>
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        onBlur={() => onChange({ ...payload, body })}
        placeholder="Ideia rápida…"
        className="h-full w-full resize-none border-0 bg-transparent p-3 text-sm font-medium leading-relaxed text-apolo-textPrimary placeholder:text-apolo-textTertiary focus:outline-none"
      />
    </div>
  );
}

function HeadingBody({ payload, onChange }) {
  const [text, setText] = useState(payload.text || '');
  const level = Math.max(1, Math.min(3, payload.level || 1));
  const Tag = `h${level}`;
  const sizeCls = level === 1 ? 'text-3xl' : level === 2 ? 'text-2xl' : 'text-xl';
  return (
    <div className="flex h-full w-full items-center px-4">
      <input
        type="text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => onChange({ ...payload, text, level })}
        placeholder="Título da seção"
        className={`w-full border-0 bg-transparent ${sizeCls} font-bold tracking-tight text-apolo-textPrimary placeholder:text-apolo-textPlaceholder focus:outline-none`}
        // Tag rendered via container; input keeps focus/edit semantics.
        aria-label={`Heading nível ${level}`}
      />
      <span className="sr-only"><Tag>{text}</Tag></span>
    </div>
  );
}

function TodoBody({ payload, onChange }) {
  const items = Array.isArray(payload.items) ? payload.items : [];
  function toggle(idx) {
    const next = items.map((i, k) => (k === idx ? { ...i, done: !i.done } : i));
    onChange({ ...payload, items: next });
  }
  function addItem() {
    onChange({ ...payload, items: [...items, { text: '', done: false }] });
  }
  function update(idx, text) {
    const next = items.map((i, k) => (k === idx ? { ...i, text } : i));
    onChange({ ...payload, items: next });
  }
  function remove(idx) {
    onChange({ ...payload, items: items.filter((_, k) => k !== idx) });
  }
  return (
    <div className="flex h-full w-full flex-col gap-1 overflow-y-auto p-3">
      {items.length === 0 ? (
        <span className="text-xs text-apolo-textTertiary">Sem tarefas ainda.</span>
      ) : null}
      {items.map((it, idx) => (
        <label key={idx} className="group flex items-center gap-2 text-sm text-apolo-textPrimary">
          <input
            type="checkbox"
            checked={!!it.done}
            onChange={() => toggle(idx)}
            className="h-4 w-4 rounded border-apolo-borderStrong accent-apolo-plan-accent"
          />
          <input
            type="text"
            value={it.text}
            onChange={(e) => update(idx, e.target.value)}
            placeholder="Nova tarefa"
            className={`flex-1 border-0 bg-transparent text-sm focus:outline-none ${it.done ? 'text-apolo-textTertiary line-through' : ''}`}
          />
          <button
            type="button"
            onClick={() => remove(idx)}
            aria-label="Remover tarefa"
            className="invisible inline-flex h-5 w-5 items-center justify-center rounded text-apolo-textTertiary hover:bg-apolo-surface3 hover:text-apolo-error group-hover:visible"
          >
            <Icons.X size={11} />
          </button>
        </label>
      ))}
      <button
        type="button"
        onClick={addItem}
        className="mt-1 inline-flex w-fit items-center gap-1 rounded text-[11px] text-apolo-plan-accent hover:underline"
      >
        <Icons.Plus size={11} /> Adicionar tarefa
      </button>
    </div>
  );
}

function ImageBody({ payload, onChange }) {
  const [url, setUrl] = useState(payload.url || '');
  if (!payload.url) {
    return (
      <div className="flex h-full w-full flex-col gap-2 p-3">
        <label className="flex flex-col gap-1">
          <span className="text-[10px] uppercase tracking-wide text-apolo-textTertiary">URL da imagem</span>
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onBlur={() => onChange({ ...payload, url })}
            placeholder="https://…"
            className="h-9 rounded-md border border-apolo-borderSubtle bg-apolo-bg px-2 text-xs focus:border-apolo-plan-accentBorder focus:outline-none"
          />
        </label>
        <span className="text-[10px] text-apolo-textTertiary">Upload direto chega no E3.</span>
      </div>
    );
  }
  return (
    <img
      src={payload.url}
      alt={payload.alt || ''}
      className="h-full w-full object-cover"
      onError={(e) => { e.currentTarget.style.opacity = '0.3'; }}
    />
  );
}

function VideoBody({ payload, onChange }) {
  const [url, setUrl] = useState(payload.url || '');
  if (!payload.url) {
    return (
      <div className="flex h-full w-full flex-col gap-2 p-3">
        <label className="flex flex-col gap-1">
          <span className="text-[10px] uppercase tracking-wide text-apolo-textTertiary">URL do vídeo</span>
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onBlur={() => onChange({ ...payload, url })}
            placeholder="YouTube, Vimeo, Loom ou MP4"
            className="h-9 rounded-md border border-apolo-borderSubtle bg-apolo-bg px-2 text-xs focus:border-apolo-plan-accentBorder focus:outline-none"
          />
        </label>
      </div>
    );
  }
  const embed = toEmbedUrl(payload.url);
  if (embed) {
    return (
      <iframe
        src={embed}
        title="vídeo"
        sandbox="allow-scripts allow-same-origin allow-presentation"
        loading="lazy"
        allowFullScreen
        className="h-full w-full border-0"
      />
    );
  }
  return (
    <video src={payload.url} controls className="h-full w-full bg-black" preload="metadata" />
  );
}

function toEmbedUrl(url) {
  try {
    const u = new URL(url);
    if (u.hostname.includes('youtube.com') || u.hostname.includes('youtu.be')) {
      const id = u.hostname.includes('youtu.be') ? u.pathname.slice(1) : u.searchParams.get('v');
      if (id) return `https://www.youtube-nocookie.com/embed/${id}`;
    }
    if (u.hostname.includes('vimeo.com')) {
      const id = u.pathname.split('/').filter(Boolean).pop();
      if (id) return `https://player.vimeo.com/video/${id}`;
    }
    if (u.hostname.includes('loom.com')) {
      const id = u.pathname.split('/').filter(Boolean).pop();
      if (id) return `https://www.loom.com/embed/${id}`;
    }
    return null;
  } catch { return null; }
}

function LinkBody({ payload, onChange }) {
  const [url, setUrl] = useState(payload.url || '');
  const [title, setTitle] = useState(payload.title || '');
  function commit() { onChange({ ...payload, url, title }); }
  return (
    <div className="flex h-full w-full flex-col gap-2 p-3">
      <input
        type="url"
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        onBlur={commit}
        placeholder="https://…"
        className="h-8 rounded-md border border-apolo-borderSubtle bg-apolo-bg px-2 text-xs focus:border-apolo-plan-accentBorder focus:outline-none"
      />
      <input
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onBlur={commit}
        placeholder="Título (opcional)"
        className="h-8 rounded-md border border-apolo-borderSubtle bg-apolo-bg px-2 text-xs focus:border-apolo-plan-accentBorder focus:outline-none"
      />
      {url ? (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-auto inline-flex items-center gap-1 truncate rounded text-[11px] font-medium text-apolo-plan-accent hover:underline"
        >
          <Icons.ExternalLink size={10} /> {title || url}
        </a>
      ) : null}
    </div>
  );
}

const FILE_ICON_BY_EXT = {
  pdf: 'FileText',
  doc: 'FileText', docx: 'FileText', txt: 'FileText', md: 'FileText', rtf: 'FileText',
  xls: 'FileSpreadsheet', xlsx: 'FileSpreadsheet', csv: 'FileSpreadsheet',
  ppt: 'Presentation', pptx: 'Presentation', key: 'Presentation',
  png: 'FileImage', jpg: 'FileImage', jpeg: 'FileImage', gif: 'FileImage', webp: 'FileImage', svg: 'FileImage', avif: 'FileImage',
  mp4: 'FileVideo', webm: 'FileVideo', mov: 'FileVideo', m4v: 'FileVideo', avi: 'FileVideo',
  mp3: 'FileAudio', wav: 'FileAudio', ogg: 'FileAudio', m4a: 'FileAudio', flac: 'FileAudio',
  zip: 'FileArchive', rar: 'FileArchive', '7z': 'FileArchive', gz: 'FileArchive', tar: 'FileArchive',
  json: 'FileCode', xml: 'FileCode', html: 'FileCode', js: 'FileCode', ts: 'FileCode', py: 'FileCode',
};
function extOfName(name) { return (/\.([a-z0-9]+)$/i.exec(name || '')?.[1] || '').toLowerCase(); }
function formatBytes(n) {
  if (!n || n < 1024) return `${n || 0} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function FileBody({ block, payload, accent }) {
  const planId = block?.plan_id;
  const fileId = payload?.file_id;
  const name = payload?.name || 'Arquivo';
  const ext = extOfName(name);
  const Icon = Icons[FILE_ICON_BY_EXT[ext]] || Icons.File;
  const size = payload?.size_bytes ? formatBytes(payload.size_bytes) : null;
  const [busy, setBusy] = useState(false);

  async function getBlobUrl() {
    if (!planId || !fileId) return null;
    const token = getStoredToken();
    const res = await fetch(
      apiUrl(`/api/plan/${encodeURIComponent(planId)}/files/${encodeURIComponent(fileId)}/download`),
      { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    );
    if (!res.ok) return null;
    return URL.createObjectURL(await res.blob());
  }
  async function openFile() {
    setBusy(true);
    const url = await getBlobUrl();
    setBusy(false);
    if (url) window.open(url, '_blank', 'noopener');
  }
  async function downloadFile() {
    setBusy(true);
    const url = await getBlobUrl();
    setBusy(false);
    if (!url) return;
    const a = document.createElement('a');
    a.href = url; a.download = name;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  }

  return (
    <div className="flex h-full w-full flex-col gap-2 p-3">
      <div className="flex items-center gap-3">
        <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md" style={{ background: `${accent}22`, color: accent }} aria-hidden="true">
          <Icon size={18} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-apolo-textPrimary">{name}</p>
          <p className="text-[11px] uppercase tracking-wide text-apolo-textTertiary">
            {ext || 'arquivo'}{size ? ` · ${size}` : ''}{!fileId ? ' · subindo…' : ''}
          </p>
        </div>
      </div>
      {fileId ? (
        <div className="mt-auto flex gap-1.5">
          <button
            type="button"
            onClick={openFile}
            disabled={busy}
            className="inline-flex flex-1 items-center justify-center gap-1 rounded-md border border-apolo-borderSubtle px-2 py-1.5 text-[11px] font-medium text-apolo-textPrimary transition hover:bg-apolo-surface2 active:scale-[0.98] disabled:opacity-60"
          >
            {busy ? <Icons.Loader2 size={11} className="animate-spin" /> : <Icons.ExternalLink size={11} />} Abrir
          </button>
          <button
            type="button"
            onClick={downloadFile}
            disabled={busy}
            className="inline-flex flex-1 items-center justify-center gap-1 rounded-md border border-apolo-borderSubtle px-2 py-1.5 text-[11px] font-medium text-apolo-textPrimary transition hover:bg-apolo-surface2 active:scale-[0.98] disabled:opacity-60"
          >
            <Icons.Download size={11} /> Baixar
          </button>
        </div>
      ) : null}
    </div>
  );
}

function SkillBody({ payload, onChange, accent }) {
  const [name, setName] = useState(payload.skill_name || '');
  const [notes, setNotes] = useState(payload.notes || '');
  function commit() { onChange({ ...payload, skill_name: name, notes }); }
  return (
    <div className="flex h-full w-full flex-col gap-2 p-3">
      <label className="flex flex-col gap-1">
        <span className="text-[10px] uppercase tracking-wide text-apolo-textTertiary">Skill</span>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={commit}
          placeholder="brainstorming, sprint-planning, …"
          className="h-8 rounded-md border border-apolo-borderSubtle bg-apolo-bg px-2 text-xs focus:border-apolo-plan-accentBorder focus:outline-none"
        />
      </label>
      <label className="flex flex-1 flex-col gap-1">
        <span className="text-[10px] uppercase tracking-wide text-apolo-textTertiary">Notas (vão pro prompt da sprint)</span>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          onBlur={commit}
          placeholder="O que essa skill deve fazer aqui?"
          className="flex-1 resize-none rounded-md border border-apolo-borderSubtle bg-apolo-bg p-2 text-xs focus:border-apolo-plan-accentBorder focus:outline-none"
        />
      </label>
      <span className="inline-flex items-center gap-1 self-start rounded-full px-2 py-0.5 text-[10px] font-medium" style={{ background: `${accent}22`, color: accent }}>
        <Icons.Sparkles size={10} /> skill block
      </span>
    </div>
  );
}

function DividerBody({ payload, onChange, accent }) {
  const [label, setLabel] = useState(payload.label || '');
  return (
    <div className="flex h-full w-full items-center gap-2 px-3">
      <span aria-hidden="true" className="h-px flex-1" style={{ background: `${accent}55` }} />
      <input
        type="text"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        onBlur={() => onChange({ ...payload, label })}
        placeholder="seção"
        className="w-24 border-0 bg-transparent text-center text-[10px] uppercase tracking-widest text-apolo-textTertiary focus:outline-none"
      />
      <span aria-hidden="true" className="h-px flex-1" style={{ background: `${accent}55` }} />
    </div>
  );
}

function PlaceholderBody({ kind, accent }) {
  const meta = getBlockKindMeta(kind);
  const Icon = Icons[meta.icon] || Icons.Square;
  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-center" style={{ color: accent }}>
      <Icon size={22} aria-hidden="true" />
      <span className="text-[11px] font-medium">{meta.label}</span>
      <span className="px-4 text-[10px] text-apolo-textTertiary">Editor rico chega no E3.</span>
    </div>
  );
}
